import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase, DJProfile, PastEvent, UpcomingEvent, GalleryItem } from '../../lib/supabase';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import PastEvents from './components/PastEvents';
import Gallery from './components/Gallery';
import MusicPlayer from './components/MusicPlayer';
import UpcomingEvents from './components/UpcomingEvents';
import SocialMedia from './components/SocialMedia';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';

export default function Home() {
  const [profile, setProfile] = useState<DJProfile | null>(null);
  const [pastEvents, setPastEvents] = useState<PastEvent[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [imageVersion, setImageVersion] = useState<number>(Date.now());
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevImagesRef = useRef<{ profile_image?: string; hero_image?: string; logo_url?: string }>({});

  const fetchProfile = useCallback(async () => {
    const { data } = await supabase.from('dj_profile').select('*').maybeSingle();
    if (data) {
      const prev = prevImagesRef.current;
      const imagesChanged =
        data.profile_image !== prev.profile_image ||
        (data as any).hero_image !== prev.hero_image ||
        (data as any).logo_url !== prev.logo_url;

      if (imagesChanged) {
        prevImagesRef.current = {
          profile_image: data.profile_image,
          hero_image: (data as any).hero_image,
          logo_url: (data as any).logo_url,
        };
        setImageVersion(Date.now());
      }

      setProfile(data);
    }
  }, []);

  const fetchPastEvents = useCallback(async () => {
    const { data } = await supabase
      .from('past_events')
      .select('*')
      .order('event_date', { ascending: false });
    if (data) setPastEvents(data);
  }, []);

  const fetchUpcomingEvents = useCallback(async () => {
    const { data } = await supabase
      .from('upcoming_events')
      .select('*')
      .gte('event_date', new Date().toISOString().split('T')[0])
      .order('event_date', { ascending: true });
    if (data) setUpcomingEvents(data);
  }, []);

  const fetchGallery = useCallback(async () => {
    const { data } = await supabase
      .from('gallery_items')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setGalleryItems(data);
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchProfile(), fetchPastEvents(), fetchUpcomingEvents(), fetchGallery()]);
  }, [fetchProfile, fetchPastEvents, fetchUpcomingEvents, fetchGallery]);

  useEffect(() => {
    fetchAll();

    const channel = supabase
      .channel('home-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dj_profile' }, () => {
        fetchProfile();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'past_events' }, () => {
        fetchPastEvents();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'upcoming_events' }, () => {
        fetchUpcomingEvents();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gallery_items' }, () => {
        fetchGallery();
      })
      .subscribe();

    pollingRef.current = setInterval(() => {
      fetchAll();
    }, 3000);

    return () => {
      supabase.removeChannel(channel);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [fetchAll, fetchProfile, fetchPastEvents, fetchUpcomingEvents, fetchGallery]);

  return (
    <div className="min-h-screen bg-black">
      <Navbar profile={profile} imageVersion={imageVersion} />
      <Hero profile={profile} imageVersion={imageVersion} />
      <PastEvents events={pastEvents} profile={profile} />
      <Gallery items={galleryItems} profile={profile} />
      <MusicPlayer profile={profile} />
      <UpcomingEvents events={upcomingEvents} profile={profile} />
      {/* SocialMedia visible solo en desktop */}
      <div className="hidden md:block">
        <SocialMedia profile={profile} />
      </div>
      <ContactForm profile={profile} />
      {/* SocialMedia visible solo en móvil, debajo del contacto */}
      <div className="block md:hidden">
        <SocialMedia profile={profile} />
      </div>
      <Footer profile={profile} imageVersion={imageVersion} />
    </div>
  );
}
